import { useCMSPageField } from '@/cms/fields'
import { useFavouriteProduct } from '@/domain/product/hooks'
import { useUser } from '@/domain/user/hooks'
import { useIntl } from '@/intl'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import { space } from '@sunwebgroup/ui-components'
import { Button, IconButton, Sheet, Typography } from '@sunwebgroup/ui-components/uds'
import * as React from 'react'
import { styled } from 'styled-components'

const FooterWrapper = styled.div`
  display: flex;
  gap: ${space('primitives_space_8')};
`

function FavoriteButton(): React.ReactElement {
  const intl = useIntl()
  const { isLoggedIn } = useUser()
  const [openSheet, setOpenSheet] = React.useState(false)
  const productId = useCMSPageField('PDP.productId')
  const contextItemId = useCMSPageField('PDP.id')
  const createAccountUrl: string = useConfiguration('links.createAccount')
  const loginUrl: string = useConfiguration('links.login')
  const { isFavourite, toggleFavouriteState } = useFavouriteProduct(productId, contextItemId)
  const returnUrl = productId
    ? encodeURIComponent(`${document.URL}&addFavorite=${productId}`)
    : encodeURIComponent(document.URL)

  const handleClick = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation()

      if (!isLoggedIn) {
        setOpenSheet(true)
        return
      }

      toggleFavouriteState()
    },
    [toggleFavouriteState, isLoggedIn],
  )

  const handleRedirect = (prefix: string) => {
    window.location.href = `${prefix}&returnurl=${returnUrl}`
    setOpenSheet(false)
  }

  return (
    <>
      <Sheet.Root open={openSheet} onOpenChange={() => setOpenSheet(!openSheet)}>
        <Sheet.Trigger>
          <IconButton
            glyph={isFavourite ? 'HeartFilled' : 'Heart'}
            type="overlay-neutral"
            ariaLabel="Add to favorites"
            size="S"
            onClick={handleClick}
          />
        </Sheet.Trigger>
        <Sheet.Body side="right">
          <Sheet.Header
            closeLabel={intl.formatMessage({ id: 'close' })}
            titleLabel={intl.formatMessage({ id: 'favorite_login_modal_title' })}
          />
          <Sheet.Content>
            <Typography>{intl.formatMessage({ id: 'favorite_login_modal_text' })}</Typography>
          </Sheet.Content>
          <Sheet.Footer>
            <FooterWrapper>
              <Button $type="secondary" onClick={() => handleRedirect(createAccountUrl)} $fullWidth>
                {intl.formatMessage({ id: 'favorite_login_modal_secondary_action_text' })}
              </Button>
              <Button $type="primary" onClick={() => handleRedirect(loginUrl)} $fullWidth>
                {intl.formatMessage({ id: 'favorite_login_modal_primary_action_text' })}
              </Button>
            </FooterWrapper>
          </Sheet.Footer>
        </Sheet.Body>
      </Sheet.Root>
    </>
  )
}

export default FavoriteButton
